import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { menuAPI, SITE_COLORS, ALLERGENS, ALLERGENS_BY_CODE } from '../../lib/api';
import { getDayName, isToday } from '../../lib/utils';
import { ArrowLeft, UtensilsCrossed, Coffee, Cookie } from 'lucide-react';
import { SEO } from '../seo/SEO';

const legacyToItems = (text) => text ? text.split(',').map(s => ({ name: s.trim(), allergens: [] })).filter(i => i.name) : [];

const MealCard = ({ label, icon, items, legacy, highlight, className = '' }) => {
  const list = (items && items.length > 0) ? items : legacyToItems(legacy);
  if (list.length === 0) return null;
  return (
    <div className={`bg-[#F7F9FC] rounded-xl p-4 ${className}`} data-testid={`meal-card-${label}`}>
      <div className="flex items-center gap-2 text-xs text-[#64748B] mb-2">{icon}{label}</div>
      <ul className="space-y-1.5">
        {list.map((m, i) => (
          <li key={i} className={`text-sm ${highlight ? 'text-[#2D3748] font-medium' : 'text-[#2D3748]'}`} data-testid={`meal-${label}-${i}`}>
            <span>{m.name}</span>
            {m.allergens?.length > 0 && (
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                {m.allergens.map((code) => (
                  <span
                    key={code}
                    title={ALLERGENS_BY_CODE[code]?.label || `Alergen ${code}`}
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FEF3C7] text-[#B45309] text-[10px] font-bold"
                    data-testid={`meal-${label}-${i}-allergen-${code}`}
                  >
                    {code}
                  </span>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const MenuPage = ({ siteKey }) => {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const colors = SITE_COLORS[siteKey];

  const dayColors = [
    { bg: `${colors.primary}15`, border: colors.primary },
    { bg: '#FFF9C4', border: '#FFB703' },
    { bg: '#F0FDF4', border: '#26DE81' },
    { bg: '#FCE4EC', border: '#FF6B6B' },
    { bg: '#E8EAF6', border: '#A55EEA' }
  ];

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const response = await menuAPI.getAll(siteKey);
        setMenu(response.data);
      } catch (error) {
        console.error('Error loading menu:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMenu();
  }, [siteKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Jídelníček"
        description={`Aktuální týdenní jídelníček ${siteKey === 'ms' ? 'MŠ Cestička' : 'dětské skupiny Chaloupka na vršku'} v Nové Roli. Vyvážená a chutná strava vč. informací o alergenech.`}
        keywords={['jídelníček', 'jídelní lístek', 'strava', 'alergeny']}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">Jídelnícek</h1>
          <p className="text-[#64748B] mt-2">Týdenní jídelní lístek</p>
        </div>

        {menu.length > 0 ? (
          <div className="space-y-4">
            {menu.map((day, index) => {
              const dColors = dayColors[index % dayColors.length];
              const today = isToday(day.date);
              
              return (
                <div 
                  key={day.id} 
                  className={`menu-day-card animate-fade-in-up ${today ? 'today' : ''}`}
                  style={{ animationDelay: `${index * 50}ms`, borderColor: today ? dColors.border : undefined }}
                  data-testid={`menu-day-${index}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    <div className="lg:w-48 flex-shrink-0">
                      <div 
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                        style={{ backgroundColor: dColors.bg, color: dColors.border }}
                      >
                        {today && <span className="w-2 h-2 rounded-full bg-current animate-pulse" />}
                        {getDayName(day.date)}
                      </div>
                      <div className="text-sm text-[#64748B] mt-1 ml-4">
                        {new Date(day.date).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' })}
                      </div>
                    </div>
                    <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <MealCard label="Snídaně" icon={<Coffee size={14} />} items={day.breakfast_items} legacy={day.breakfast} />
                      <MealCard label="Svačina" icon={<Cookie size={14} />} items={day.snack_am_items} legacy={day.snack_am} />
                      <MealCard label="Oběd" icon={<UtensilsCrossed size={14} />} items={day.lunch_items} legacy={day.lunch} highlight className="sm:col-span-2 lg:col-span-1" />
                      <MealCard label="Odp. svačina" icon={<Cookie size={14} />} items={day.snack_pm_items} legacy={day.snack_pm} />
                    </div>
                  </div>
                  {day.notes && (
                    <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-[#64748B]">
                      Poznámka: {day.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border-3 border-[#F0F4F8]">
            <UtensilsCrossed size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <div className="text-[#64748B]">Jídelnícek zatím není k dispozici</div>
          </div>
        )}

        <div className="mt-8 bg-[#FFF9C4] rounded-2xl p-6">
          <h3 className="font-semibold text-[#92400E] mb-2">Informace o stravování</h3>
          <p className="text-sm text-[#92400E]/80">
            Strava je připravována přímo v naší školní kuchyni z čerstvých surovin.
            V případě alergie nebo speciálních dietních požadavků nás prosím kontaktujte.
          </p>
        </div>

        <div className="mt-4 bg-white rounded-2xl p-6 border-2 border-[#F0F4F8]" data-testid="allergens-legend">
          <h3 className="font-semibold text-[#2D3748] mb-3">Seznam alergenů</h3>
          <p className="text-xs text-[#64748B] mb-3">Čísla u jídel označují přítomné alergeny podle EU směrnice 1169/2011.</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {ALLERGENS.map(a => (
              <div key={a.code} className="flex items-center gap-2 text-sm">
                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-[#FEF3C7] text-[#B45309] text-xs font-bold">{a.code}</span>
                <span className="text-[#2D3748]">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuPage;
